import { ReactNode } from 'react';
import { Navigate } from 'hotel/navigation';

import { useAppSelector } from '../store/store';

type InactiveClientRouteGuardProps = {
  children: ReactNode;
};

/**
 * InactiveClientRouteGuard component prevents access to entire routes
 * when the client account is inactive.
 *
 * Redirects to dashboard with a message instead of rendering the route.
 *
 * @example
 * // Protect a route from inactive clients
 * <Route
 *   path="group_request/*"
 *   element={
 *     <InactiveClientRouteGuard>
 *       <GroupRequest />
 *     </InactiveClientRouteGuard>
 *   }
 * />
 */
export const InactiveClientRouteGuard = ({
  children,
}: InactiveClientRouteGuardProps) => {
  const client = useAppSelector((state) => state.user.profile?.client);

  // If client is inactive, redirect to dashboard
  // Banner will already be showing to explain why
  if (client?.isInactive) {
    return <Navigate to="/dashboard" replace />;
  }

  // Client is active, render the protected route
  return <>{children}</>;
};

export default InactiveClientRouteGuard;
